import { useCallback, useRef } from 'react'

import {
	useLazyGetChildrenQuery,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { useLazyGetFullTreeV2Query } from '@/rtkApi/mddApiV2Enhanced'
import { logError } from '@/utils/errorHandling'

import { toTreeItem } from '../../constants'
import { StructureTreeItem } from '../../types'
import { processChildrenResponse } from './treeDataUtils'
import { TreeDataState } from './types'
import { useRevealItem } from './useRevealItem'

interface UseTreeDataOperationsOptions {
	childrenMap: Record<string, StructureTreeItem[]>,
	onBatchLoaded?: (nodes: StructureTreeItem[]) => void
	onChildrenLoaded?: (parentId: number, children: StructureTreeItem[]) => void,
	systems: StructureTreeItem[]
}

interface UseTreeDataOperationsReturn {
	findItem: (id: string) => StructureTreeItem | undefined,
	loadChildren: (parentId: string, forceRefresh?: boolean, silent?: boolean) => Promise<void>,
	refresh: (expandedItems: string[]) => Promise<void>,
	refreshSystems: () => Promise<void>,
	/** Full tree refresh without replacing visible tree content (runs in background) */
	refreshSystemsSilent: () => Promise<void>,
	revealItem: (
		itemId: string,
		expandedItems: string[],
		onExpand: (newExpanded: string[]) => void,
	) => Promise<void>
}

export const useTreeDataOperations = (
	state: TreeDataState,
	options: UseTreeDataOperationsOptions,
): UseTreeDataOperationsReturn => {
	const {
		rawChildrenMap,
		setRawChildrenMap,
		setRawSystems,
		loadingIds,
		setLoadingIds,
		setUsedIds,
		setIsFullTreeLoading,
	} = state

	const { onChildrenLoaded, onBatchLoaded, systems, childrenMap } = options

	const [fetchChildren] = useLazyGetChildrenQuery()
	const [fetchFullTree] = useLazyGetFullTreeV2Query()

	const childrenMapRef = useRef(rawChildrenMap)
	childrenMapRef.current = rawChildrenMap

	const revealItem = useRevealItem({
		systems,
		childrenMap,
		setUsedIds,
		setRawChildrenMap,
	})

	const loadChildren = useCallback(
		async (parentId: string, forceRefresh = false, silent = false) => {
			if (!silent && loadingIds.has(parentId)) return

			if (!forceRefresh && childrenMapRef.current[parentId] !== undefined) {
				return
			}

			if (!silent) {
				setLoadingIds((prev) => new Set(prev).add(parentId))
			}

			try {
				const result = await fetchChildren({
					parentId: Number(parentId),
					maxLevel: 100,
				}).unwrap()
				const { children, grandchildrenByParent, leafNodeEntries } =
					processChildrenResponse(result, Number(parentId))

				setRawChildrenMap((prev) => ({
					...prev,
					[parentId]: children,
					...grandchildrenByParent,
					...leafNodeEntries,
				}))

				onChildrenLoaded?.(Number(parentId), children)
				Object.entries(grandchildrenByParent).forEach(
					([childId, grandchildren]) => {
						onChildrenLoaded?.(Number(childId), grandchildren)
					},
				)
			} catch (error) {
				logError(error, {
					operation: 'useTreeDataOperations.loadChildren',
					details: { parentId },
				})
				setRawChildrenMap((prev) => ({ ...prev, [parentId]: [] }))
			} finally {
				if (!silent) {
					setLoadingIds((prev) => {
						const next = new Set(prev)
						next.delete(parentId)
						return next
					})
				}
			}
		},
		[fetchChildren, loadingIds, onChildrenLoaded, setLoadingIds, setRawChildrenMap],
	)

	const refresh = useCallback(
		async (expandedItems: string[]) => {
			setRawChildrenMap({})
			setUsedIds(new Set())
			for (const itemId of expandedItems) {
				await loadChildren(itemId, true)
			}
		},
		[loadChildren, setRawChildrenMap, setUsedIds],
	)

	const doFullTreeRefresh = useCallback(async (silent: boolean) => {
		setIsFullTreeLoading(true)
		try {
			const result = await fetchFullTree({}).unwrap()

			const grouped: Record<string, StructureTreeItem[]> = {}
			const systemItems: StructureTreeItem[] = []
			const allNodes: StructureTreeItem[] = []
			const nodeIdsWithChildren = new Set<string>()

			result.forEach((item) => {
				const treeItem = toTreeItem(item)
				if (item.type === 'SYSTEM') {
					systemItems.push(treeItem)
				} else {
					const parentId = String(item.parent?.id)
					nodeIdsWithChildren.add(parentId)
					if (!grouped[parentId]) {
						grouped[parentId] = []
					}
					grouped[parentId].push(treeItem)
					allNodes.push(treeItem)
				}
			})

			Object.values(grouped).forEach((children) => {
				children.forEach((child) => {
					child.hasChildren = nodeIdsWithChildren.has(child.id)
				})
			})
			systemItems.forEach((system) => {
				system.hasChildren = nodeIdsWithChildren.has(system.id)
			})

			setRawSystems(systemItems)
			setRawChildrenMap(grouped)

			onBatchLoaded?.(allNodes)
		} catch (error) {
			logError(error, {
				operation: silent ? 'useTreeDataOperations.refreshSystemsSilent' : 'useTreeDataOperations.refreshSystems',
			})
		} finally {
			setIsFullTreeLoading(false)
		}
	}, [fetchFullTree, onBatchLoaded, setIsFullTreeLoading, setRawChildrenMap, setRawSystems])

	const refreshSystems = useCallback(() => doFullTreeRefresh(false), [doFullTreeRefresh])
	const refreshSystemsSilent = useCallback(() => doFullTreeRefresh(true), [doFullTreeRefresh])

	const findItem = useCallback(
		(id: string): StructureTreeItem | undefined =>
			systems.find((s) => s.id === id) ??
			Object.values(childrenMap)
				.flat()
				.find((c) => c.id === id),
		[systems, childrenMap],
	)

	return {
		loadChildren,
		refresh,
		refreshSystems,
		refreshSystemsSilent,
		revealItem,
		findItem,
	}
}
